import { useEffect, useRef, MutableRefObject, useCallback } from 'react';
import { withWindow } from '@shared/utils/withWindow';

/**
 * Returns a mutable ref object whose `.current` property is always the latest value.
 * @param value The value to keep updated in the ref.
 * @returns A ref object with the latest value.
 */
function useLatest<T>(value: T): MutableRefObject<T> {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

/**
 * Options for the usePolling hook.
 */
export interface UsePollingOptions {
  /**
   * Determines whether polling should continue for the given poll count.
   * @param pollCount The number of completed polls.
   * @returns True to continue polling, false to stop.
   */
  readonly shouldPoll?: (pollCount: number) => boolean;
  /**
   * Function to call on each poll interval.
   * Can be async.
   */
  readonly onPoll: () => Promise<unknown> | unknown;
  /**
   * Optional error handler for poll failures.
   */
  readonly onError?: (error: unknown) => void;
  /**
   * Optional timeout handler called when maxWaitMs is exceeded.
   */
  readonly onTimeout?: () => void;
  /**
   * Array of polling intervals in milliseconds.
   * The schedule advances after each poll, using the final value for remaining polls
   * If empty or not provided, defaults to [10000].
   */
  readonly pollScheduleMs?: readonly number[];
  /**
   * Maximum time in milliseconds to poll before timing out.
   * Defaults to 300,000 ms (5 minutes).
   */
  readonly maxWaitMs?: number;
  /**
   * If true, polling starts immediately without waiting for the first interval.
   */
  readonly immediate?: boolean;
}

/**
 * Return value from usePolling hook.
 */
export interface UsePollingReturn {
  /**
   * Restarts polling from the beginning.
   * All state (poll count, start time, timeout state) will be reset.
   */
  restart: () => void;
}

/**
 * React hook for polling at specified intervals with timeout and error handling.
 * Polling pauses when the tab becomes inactive and resumes intelligently when active again.
 *
 * @param options Configuration for polling behavior.
 *
 * @example
 * usePolling({
 *   shouldPoll: count => count < 5,
 *   onPoll: async () => { ... },
 *   onError: err => { ... },
 *   onTimeout: () => { ... },
 *   pollScheduleMs: [1000, 2000, 5000],
 *   maxWaitMs: 10000,
 *   immediate: true
 * });
 */
export function usePolling(options: Readonly<UsePollingOptions>): UsePollingReturn {
  const { shouldPoll, onPoll, onError, onTimeout, immediate = false } = options;

  const maxWaitMs = options.maxWaitMs ?? 300_000;
  const initialSchedule = options.pollScheduleMs && options.pollScheduleMs.length > 0 ? options.pollScheduleMs : [10_000];
  const scheduleRef = useLatest(initialSchedule);

  const onPollRef = useLatest(onPoll);
  const onErrorRef = useLatest(onError);
  const onTimeoutRef = useLatest(onTimeout);
  const shouldPollRef = useLatest(shouldPoll);

  const pollCountRef = useRef(0);
  const activeRef = useRef(false);
  const startTimeRef = useRef(0);
  // keep track of the last poll time letting us cancel and restart polling on window focus change
  // this approach avoids unnecessary polling in the background
  const lastPollTimeRef = useRef(0);
  const isTabVisibleRef = useRef(true);
  const timeoutIdRef = useRef(0);
  const timeoutFiredRef = useRef(false);
  const tickFnRef = useRef<(() => Promise<void>) | null>(null);
  const scheduleNextFnRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    /**
     * Effect cleanup: stops polling and clears any scheduled timeouts.
     */
    return withWindow(global => {
      activeRef.current = true;
      pollCountRef.current = 0;
      startTimeRef.current = Date.now();
      lastPollTimeRef.current = 0;
      isTabVisibleRef.current = !global.document.hidden;

      timeoutIdRef.current = 0;
      timeoutFiredRef.current = false;

      /**
       * Returns elapsed time (ms) since polling started.
       */
      const elapsed = () => Date.now() - startTimeRef.current;

      /**
       * Returns true if the total elapsed time has reached/exceeded maxWaitMs.
       * Fires onTimeout once (idempotent) and prevents further scheduling.
       */
      const timedOut = () => {
        if (!timeoutFiredRef.current && elapsed() >= maxWaitMs) {
          timeoutFiredRef.current = true;
          if (activeRef.current) onTimeoutRef.current?.();
          return true;
        }
        return false;
      };

      /**
       * Gets the current interval based on poll count and schedule.
       */
      const getCurrentInterval = (): number => {
        const lastIndex = scheduleRef.current.length - 1;
        const completed = pollCountRef.current;
        const offset = immediate ? 1 : 0;
        const index = Math.min(Math.max(completed - offset, 0), lastIndex);
        return scheduleRef.current[Math.max(index, 0)] ?? 10_000;
      };

      /**
       * Schedules the next tick according to pollScheduleMs and remaining time.
       * No-ops if inactive, tab not visible, or already timed out.
       */
      function scheduleNext(): void {
        if (!activeRef.current || !isTabVisibleRef.current) return;
        if (timedOut()) return;

        const remaining = maxWaitMs - elapsed();
        const baseDelay = getCurrentInterval();
        const delay = Math.min(baseDelay, remaining);

        if (delay > 0) {
          timeoutIdRef.current = global.setTimeout(tick, delay);
        }
      }

      /**
       * Executes a single polling cycle and schedules the next one.
       */
      async function tick(): Promise<void> {
        if (!activeRef.current || !isTabVisibleRef.current) return;
        if (timedOut()) return;

        // if shouldPoll is provided, check if we should continue polling
        if (shouldPollRef.current && !shouldPollRef.current(pollCountRef.current)) {
          // stop polling, shouldPoll returned false
          activeRef.current = false;
          global.clearTimeout(timeoutIdRef.current);
          return;
        }

        try {
          await onPollRef.current();
          lastPollTimeRef.current = Date.now();
        } catch (e) {
          if (activeRef.current) onErrorRef.current?.(e);
        }

        if (!activeRef.current) return;

        pollCountRef.current += 1;
        scheduleNext();
      }

      /**
       * Handles tab visibility changes. When tab becomes visible again,
       * checks if enough time has passed to warrant immediate polling.
       * This ensures we are not unnecessarily polling in the background.
       * It also ensures that the polling immediately resumes if the tab
       * becomes visible again after being hidden for a while (longer than the interval).
       */
      function handleVisibilityChange(): void {
        const wasVisible = isTabVisibleRef.current;
        isTabVisibleRef.current = !global.document.hidden;

        // Tab became visible again
        if (!wasVisible && isTabVisibleRef.current && activeRef.current) {
          if (timedOut()) return;

          const now = Date.now();
          const timeSinceLastPoll = now - lastPollTimeRef.current;
          const currentInterval = getCurrentInterval();

          // If we haven't polled yet, poll immediately; otherwise, poll if enough time has passed
          if (lastPollTimeRef.current === 0) {
            // Poll immediately
            void tick();
          } else if (timeSinceLastPoll >= currentInterval) {
            // Poll immediately (the interval elapsed)
            void tick();
          } else {
            // Schedule the next poll for the remaining time
            const remainingDelay = currentInterval - timeSinceLastPoll;
            const maxRemaining = maxWaitMs - elapsed();
            const delay = Math.min(remainingDelay, maxRemaining);

            if (delay > 0) {
              timeoutIdRef.current = global.setTimeout(tick, delay);
            }
          }
        }

        // Tab became hidden - clear any pending timeouts
        if (wasVisible && !isTabVisibleRef.current) {
          global.clearTimeout(timeoutIdRef.current);
        }
      }

      // Set up visibility change listener
      global.document.addEventListener('visibilitychange', handleVisibilityChange);

      // Store functions in refs so they can be accessed from outside the effect
      tickFnRef.current = tick;
      scheduleNextFnRef.current = scheduleNext;

      // Start polling if tab is visible
      if (isTabVisibleRef.current) {
        if (immediate) {
          void tick();
        } else {
          scheduleNext();
        }
      }

      /**
       * Cleanup: marks polling as inactive, removes listeners, and clears timeouts.
       */
      return () => {
        activeRef.current = false;
        global.document.removeEventListener('visibilitychange', handleVisibilityChange);
        global.clearTimeout(timeoutIdRef.current);
        tickFnRef.current = null;
        scheduleNextFnRef.current = null;
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxWaitMs, immediate]);

  // Restart function that resets state and restarts polling
  // Defined outside useEffect so it can properly reset external state
  const restart = useCallback(() => {
    withWindow(global => {
      global.clearTimeout(timeoutIdRef.current);
      activeRef.current = true;
      pollCountRef.current = 0;
      startTimeRef.current = Date.now();
      lastPollTimeRef.current = 0;
      timeoutFiredRef.current = false;

      // Restart polling
      if (isTabVisibleRef.current) {
        if (immediate) {
          tickFnRef.current?.();
        } else {
          scheduleNextFnRef.current?.();
        }
      }
      return () => {};
    });
  }, [immediate]);

  return { restart };
}
